import { useEffect, useState } from "react"
import React from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { UsageDataDto } from "../Dtos/AppUsageDashboard/UsageDataDto";
import { useCookies } from "react-cookie";
import { DEFAULT_HEADERS, to_API_URL } from "../Services/RestService";

export interface WelcomeDashboardProps {
    usageData: UsageDataDto
}

const WelcomeDashboard: React.FC<WelcomeDashboardProps> = (props) => {
    const [cookies] = useCookies(["accessToken"]);
    const [usageData, setUsageData] = useState<UsageDataDto | null>(null);
    async function initialize() {
        const result = await fetch(to_API_URL("api/v1/dashboard/welcome"),
            {

                method: "GET",
                headers: {
                    ...DEFAULT_HEADERS,
                    "Authorization": "Bearer " + cookies.accessToken,
                }

            }
        );
        if (result.ok) {
            const data = await result.json() as UsageDataDto;
            setUsageData(data);
        }

    }

    useEffect(() => {
        initialize();
    }, [])

    // Destructure the usageData prop
    const {
        totalUsers,
        registrationsLast24H,
        registrationsLastWeek,
        registrationsLastMonth,
        usersWithoutEmailValidation,
        totalAppointments,
        totalDailyReports
    } = usageData || {
        totalUsers: 0,
        registrationsLast24H: 0,
        registrationsLastWeek: 0,
        registrationsLastMonth: 0,
        usersWithoutEmailValidation: 0,
        totalAppointments: 0,
        totalDailyReports: 0

    };

    // Format large numbers with commas
    const formatNumber = (num: number) => {
        if (num === undefined || num === null) return 'N/A';
        return num.toLocaleString();
    };

    // Calculate percentages
    const emailValidationRate = totalUsers > 0
        ? ((totalUsers - usersWithoutEmailValidation) / totalUsers * 100).toFixed(1)
        : 0;

    const reportsPerUser = totalUsers > 0
        ? (totalDailyReports / totalUsers).toFixed(2)
        : 0;

    const appointmentsPerUser = totalUsers > 0
        ? (totalAppointments / totalUsers).toFixed(2)
        : 0;

    // Data for registration trends chart
    const registrationData = [
        { name: 'Last 24H', value: registrationsLast24H || 0 },
        { name: 'Last Week', value: registrationsLastWeek || 0 },
        { name: 'Last Month', value: registrationsLastMonth || 0 }
    ];

    // Data for user validation status pie chart
    const validationData = [
        { name: 'Validated', value: totalUsers ? totalUsers - usersWithoutEmailValidation : 0 },
        { name: 'Not Validated', value: usersWithoutEmailValidation || 0 }
    ];

    // Colors for charts
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <header className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-800">Application Usage Dashboard</h1>
                    <p className="text-gray-600 mt-2">Overview of user activity and system metrics</p>
                </header>

                {/* Key Metrics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-gray-500 text-sm font-medium uppercase">Total Users</p>
                                <p className="text-2xl font-semibold text-gray-800">{formatNumber(totalUsers)}</p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <p className="text-sm text-gray-600">
                                <span className="text-green-500 font-semibold">+{formatNumber(registrationsLast24H)} </span>
                                new users in the last 24h
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full bg-green-100 text-green-600">
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-gray-500 text-sm font-medium uppercase">Email Validation Rate</p>
                                <p className="text-2xl font-semibold text-gray-800">{emailValidationRate}%</p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <p className="text-sm text-gray-600">
                                <span className="text-red-500 font-semibold">{formatNumber(usersWithoutEmailValidation)} </span>
                                users pending validation
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-gray-500 text-sm font-medium uppercase">Total Appointments</p>
                                <p className="text-2xl font-semibold text-gray-800">{formatNumber(totalAppointments)}</p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <p className="text-sm text-gray-600">
                                <span className="font-semibold">{appointmentsPerUser} </span>
                                appointments per user
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-gray-500 text-sm font-medium uppercase">Daily Reports</p>
                                <p className="text-2xl font-semibold text-gray-800">{formatNumber(totalDailyReports)}</p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <p className="text-sm text-gray-600">
                                <span className="font-semibold">{reportsPerUser} </span>
                                reports per user
                            </p>
                        </div>
                    </div>
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Registration Trends */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Registration Trends</h2>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={registrationData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="value" name="New Registrations" fill="#0088FE" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Email Validation Status */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Email Validation Status</h2>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={validationData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    outerRadius={100}
                                    fill="#8884d8"
                                    dataKey="value"
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                >
                                    {validationData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Activity Metrics */}
                <div className="bg-white rounded-lg shadow p-6 mb-8">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Activity Summary</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="p-4 border rounded-lg">
                            <h3 className="text-gray-500 text-sm font-medium uppercase mb-2">Monthly Registration Growth</h3>
                            <div className="flex items-baseline">
                                <p className="text-3xl font-bold text-gray-800">
                                    {registrationsLastMonth ? formatNumber(registrationsLastMonth) : 'N/A'}
                                </p>
                                <p className="ml-2 text-sm text-green-500">
                                    +{registrationsLastWeek ? (registrationsLastWeek / registrationsLastMonth * 100).toFixed(1) : 0}%
                                </p>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">Compared to previous month</p>
                        </div>

                        <div className="p-4 border rounded-lg">
                            <h3 className="text-gray-500 text-sm font-medium uppercase mb-2">Appointments Created</h3>
                            <p className="text-3xl font-bold text-gray-800">{formatNumber(totalAppointments)}</p>
                            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                                <div
                                    className="bg-blue-600 h-2.5 rounded-full"
                                    style={{ width: `${Math.min((totalAppointments / (totalUsers || 1)) * 10, 100)}%` }}
                                ></div>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{appointmentsPerUser} per user on average</p>
                        </div>

                        <div className="p-4 border rounded-lg">
                            <h3 className="text-gray-500 text-sm font-medium uppercase mb-2">Daily Reports Submitted</h3>
                            <p className="text-3xl font-bold text-gray-800">{formatNumber(totalDailyReports)}</p>
                            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                                <div
                                    className="bg-green-600 h-2.5 rounded-full"
                                    style={{ width: `${Math.min((totalDailyReports / (totalUsers || 1)) * 10, 100)}%` }}
                                ></div>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{reportsPerUser} per user on average</p>
                        </div>
                    </div>
                </div>

                {/* Footer Info */}
                <footer className="bg-white rounded-lg shadow p-6">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <div className="mb-4 md:mb-0">
                            <h2 className="text-lg font-semibold text-gray-800">System Health</h2>
                            <p className="text-gray-600">All systems operational</p>
                        </div>
                        <div className="flex space-x-4">
                            <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                                Export Data
                            </button>
                            <button className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors">
                                Refresh
                            </button>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default WelcomeDashboard;